import React, { useMemo } from 'react';
import { Clock } from 'lucide-react';
import { formatTimePT } from '../../../utils/dateTime';

export default function Timeline({ incident }) {
  const timelineEvents = useMemo(() => {
    // Collect alerts from every location the backend may nest them
    const alerts =
      incident?.alerts ||
      incident?._raw?.alerts ||
      incident?._raw?.Alerts ||
      [];

    if (!alerts.length) return [];

    const colorMap = {
      critical: "#dc2626",
      high:     "#ea580c",
      medium:   "#f59e0b",
      informational: "#60a5fa",
      low:      "#10b981",
      default:  "#94a3b8",
    };

    return alerts
      .map((alert) => {
        // Azure Sentinel uses several timestamp field names — try them all
        const time =
          alert.timeGenerated    ||
          alert.TimeGenerated    ||
          alert.timeGeneratedUtc ||
          alert.TimeGeneratedUtc ||
          alert.startTimeUtc     ||
          alert.StartTimeUtc     ||
          alert.startTime        ||
          alert.endTimeUtc       ||
          alert.EndTimeUtc       ||
          alert.endTime          ||
          alert.processingEndTime||
          alert.createdAt        ||
          alert.CreatedAt        ||
          null;

        // Skip alerts with no usable timestamp — they can't be placed on a timeline
        if (!time) return null;

        const description =
          alert.displayName  ||
          alert.DisplayName  ||
          alert.alertName    ||
          alert.AlertName    ||
          alert.name         ||
          alert.Name         ||
          alert.title        ||
          alert.Title        ||
          alert.description  ||
          alert.Description  ||
          "Security alert";

        const rawSeverity =
          alert.alertSeverity ||
          alert.AlertSeverity ||
          alert.severity      ||
          alert.Severity      ||
          "medium";

        const severity = rawSeverity.toString().toLowerCase();

        return {
          time,
          description,
          type: alert.type || alert.Type || alert.intent || "alert",
          severity,
          color: colorMap[severity] || colorMap.default,
        };
      })
      .filter(Boolean)
      .sort((a, b) => new Date(a.time) - new Date(b.time));
  }, [incident]);

  const getSeverityClass = (severity) => {
    switch (severity) {
      case 'critical': return 'chip chip--danger';
      case 'high': return 'chip chip--warn';
      case 'medium': return 'chip chip--medium';
      case 'low': return 'chip chip--low';
      default: return 'chip';
    }
  };

  const getTypeClass = (type) => {
    switch (type) {
      case 'detection': return 'chip chip--muted';
      case 'alert': return 'chip chip--muted';
      case 'analysis': return 'chip chip--muted';
      case 'response': return 'chip chip--muted';
      case 'investigation': return 'chip chip--muted';
      default: return 'chip chip--muted';
    }
  };

  return (
    <div className="timeline">
      <div className="timeline__section">
        <h3 className="timeline__section-title">Incident Timeline</h3>
        <div className="timeline-events">
          {timelineEvents.length ? (
            timelineEvents.map((event, index) => (
              <div key={index} className="timeline-event">
                <div className="timeline-event__indicator">
                  <div 
                    className="timeline-event__dot" 
                    style={{ backgroundColor: event.color }}
                  ></div>
                  {index < timelineEvents.length - 1 && (
                    <div className="timeline-event__line"></div>
                  )}
                </div>
                
                <div className="timeline-event__content">
                  <div className="timeline-event__header">
                    <div className="timeline-event__time">
                      <Clock size={14} />
                      {formatTimePT(event.time)}
                      <span style={{ fontSize: '10px', opacity: 0.55, marginLeft: 4 }}>
                        {new Date(event.time).toLocaleDateString('en-US', { timeZone: 'America/Los_Angeles', month: 'short', day: 'numeric' })}
                      </span>
                    </div>
                    <div className="timeline-event__badges">
                      <span className={getTypeClass(event.type)}>{event.type}</span>
                      <span className={getSeverityClass(event.severity)}>{event.severity}</span>
                    </div>
                  </div>
                  <div className="timeline-event__description">
                    {event.description}
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="text-muted">
              No timeline data available.
              {process.env.NODE_ENV === 'development' && (
                <div style={{ fontSize: 11, marginTop: 6, color: '#94a3b8' }}>
                  Debug: incident.alerts = {JSON.stringify(incident?.alerts?.length ?? 'undefined')},
                  _raw.alerts = {JSON.stringify(incident?._raw?.alerts?.length ?? 'undefined')}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
